import { calculateLogTicks, scaleValueToRatio } from "../coordinates/priceScale";
function normalizeDomain(domain) {
    if (!domain) {
        return null;
    }
    const [rawMin, rawMax] = domain;
    if (!Number.isFinite(rawMin) || !Number.isFinite(rawMax)) {
        return null;
    }
    if (rawMin === rawMax) {
        const delta = Math.max(1, Math.abs(rawMin) * 0.01);
        return [rawMin - delta, rawMax + delta];
    }
    return rawMin < rawMax ? [rawMin, rawMax] : [rawMax, rawMin];
}
function resolveThemePalette(mode, gridColor, gridSubtleColor) {
    const palette = mode === "dark"
        ? { grid: "rgba(255, 255, 255, 0.08)", gridSubtle: "rgba(255, 255, 255, 0.04)", axisBorder: "rgba(255, 255, 255, 0.08)", label: "#787b86" }
        : { grid: "rgba(150, 160, 180, 0.12)", gridSubtle: "rgba(150, 160, 180, 0.06)", axisBorder: "#e2e8f0", label: "#787b86" };
    if (gridColor)
        palette.grid = gridColor;
    if (gridSubtleColor)
        palette.gridSubtle = gridSubtleColor;
    return palette;
}
function formatAxisLabel(value) {
    const abs = Math.abs(value);
    if (!Number.isFinite(value))
        return "0.00";
    return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function calculateTicks(domain, height) {
    const [min, max] = domain;
    if (min >= max || !Number.isFinite(min) || !Number.isFinite(max)) {
        return [];
    }
    const range = max - min;
    const targetTickCount = Math.max(2, Math.floor(height / 50));
    const rawInterval = range / targetTickCount;
    if (rawInterval <= 0) {
        return [];
    }
    const power = Math.floor(Math.log10(rawInterval));
    const base = Math.pow(10, power);
    const ratio = rawInterval / base;
    let step = base;
    if (ratio < 1.5) {
        step = base;
    }
    else if (ratio < 3.5) {
        step = 2 * base;
    }
    else if (ratio < 7.5) {
        step = 5 * base;
    }
    else {
        step = 10 * base;
    }
    const firstTick = Math.ceil(min / step) * step;
    const ticks = [];
    const epsilon = step * 1e-9;
    for (let val = firstTick; val <= max + epsilon; val += step) {
        if (ticks.length > 50) {
            break;
        }
        ticks.push(val);
    }
    return ticks;
}
function yForValue(value, domain, bounds, scale) {
    const [min, max] = domain;
    const range = max - min;
    if (range <= 0 && (scale?.mode ?? "linear") === "linear") {
        return bounds.top + bounds.height / 2;
    }
    const ratio = scaleValueToRatio(value, domain, scale);
    return Math.round(bounds.top + bounds.height - ratio * bounds.height);
}
function percentTicks(domain, base, height) {
    const toPct = (v) => ((v - base) / Math.abs(base)) * 100;
    const pctDomain = [toPct(domain[0]), toPct(domain[1])];
    const lo = Math.min(pctDomain[0], pctDomain[1]);
    const hi = Math.max(pctDomain[0], pctDomain[1]);
    return calculateTicks([lo, hi], height).map((pct) => ({
        price: base + (pct / 100) * Math.abs(base),
        label: pct,
    }));
}
function renderAxisLabels(ctx, side, axis, palette, labelFormatter) {
    const domain = normalizeDomain(axis.domain);
    if (!domain) {
        return;
    }
    const scale = axis.scale;
    const base = scale?.mode === "percent" && typeof scale.base === "number" && Number.isFinite(scale.base) && scale.base !== 0
        ? scale.base
        : null;
    // Ticks carry {price (for positioning), label (for text)} so percent mode
    // shows % values while positioning through the price-space transform.
    const ticks = base !== null
        ? percentTicks(domain, base, axis.bounds.height)
        : (scale?.mode === "log"
            ? calculateLogTicks(domain, axis.bounds.height).map((price) => ({ price, label: price }))
            : calculateTicks(domain, axis.bounds.height).map((price) => ({ price, label: price })));
    const fmt = labelFormatter ?? formatAxisLabel;
    const suffix = !labelFormatter && base !== null ? "%" : "";
    ctx.save();
    ctx.fillStyle = palette.label;
    ctx.font = "10px sans-serif";
    ctx.textBaseline = "middle";
    for (const tick of ticks) {
        const y = yForValue(tick.price, domain, axis.bounds, scale);
        if (y < axis.bounds.top + 4 || y > axis.bounds.top + axis.bounds.height - 4) {
            continue;
        }
        const label = `${fmt(tick.label)}${suffix}`;
        // AXIS-ALIGN: every right-gutter label is LEFT-anchored at a uniform
        // 4px inset (user requirement — mixed outer/inner anchoring looked
        // ragged). The fixed 48px gutter fits grouped numbers up to ~42px
        // ("600,000"); freak widths fall back to right-anchoring. Nothing may
        // spill into the plot. Missing measureText (partial contexts) falls
        // back to legacy outer-edge anchoring.
        const w = typeof ctx.measureText === "function" ? ctx.measureText(label).width : 0;
        let x;
        if (side === "left") {
            if (w <= axis.bounds.width - 10) {
                ctx.textAlign = "left";
                x = axis.bounds.left + 8;
            }
            else {
                ctx.textAlign = "right";
                x = axis.bounds.left + axis.bounds.width - 2;
            }
        }
        else {
            if (w <= axis.bounds.width - 6) {
                ctx.textAlign = "left";
                x = axis.bounds.left + 4;
            }
            else {
                ctx.textAlign = "right";
                x = axis.bounds.left + axis.bounds.width - 2;
            }
        }
        ctx.fillText(label, x, y);
    }
    ctx.restore();
}
function parseDashArray(input) {
    if (!input || input.trim() === "")
        return [];
    const parts = input.split(",").map((s) => Number(s.trim()));
    if (parts.some((p) => !Number.isFinite(p) || p < 0))
        return [];
    return parts;
}
function renderHorizontalGridLines(ctx, plotBounds, gridDomain, palette, gridDash, gridWidth, scale) {
    const ticks = scale?.mode === "log"
        ? calculateLogTicks(gridDomain, plotBounds.height)
        : scale?.mode === "percent" && typeof scale.base === "number" && Number.isFinite(scale.base) && scale.base !== 0
            ? percentTicks(gridDomain, scale.base, plotBounds.height).map((tick) => tick.price)
            : calculateTicks(gridDomain, plotBounds.height);
    ctx.save();
    ctx.lineWidth = gridWidth ?? 0.5;
    ctx.setLineDash(parseDashArray(gridDash));
    ctx.strokeStyle = palette.grid;
    for (const val of ticks) {
        const y = yForValue(val, gridDomain, plotBounds, scale);
        if (y < plotBounds.top + 2 || y > plotBounds.top + plotBounds.height - 2)
            continue;
        ctx.beginPath();
        ctx.moveTo(plotBounds.left, y);
        ctx.lineTo(plotBounds.left + plotBounds.width, y);
        ctx.stroke();
    }
    ctx.restore();
}
function renderVerticalGridLines(ctx, plotBounds, barRange, palette, gridDash, gridWidth) {
    const { from, to } = barRange;
    const barCount = to - from;
    if (barCount <= 0 || !Number.isFinite(barCount))
        return;
    const targetLineCount = Math.max(3, Math.floor(plotBounds.width / 90));
    const step = Math.max(1, Math.floor(barCount / targetLineCount));
    ctx.save();
    ctx.lineWidth = gridWidth ?? 0.5;
    ctx.setLineDash(parseDashArray(gridDash));
    ctx.strokeStyle = palette.grid;
    for (let i = 0; i <= barCount; i += step) {
        const ratio = i / barCount;
        const x = Math.round(plotBounds.left + ratio * plotBounds.width);
        ctx.beginPath();
        ctx.moveTo(x, plotBounds.top);
        ctx.lineTo(x, plotBounds.top + plotBounds.height);
        ctx.stroke();
    }
    ctx.restore();
}
/** @internal */
export class AxisGridRenderer {
    render(ctx, options) {
        const themeMode = options.themeMode ?? "light";
        const palette = resolveThemePalette(themeMode, options.gridColor, options.gridSubtleColor);
        const labelColor = options.labelColor ?? palette.label;
        const activePalette = { ...palette, label: labelColor };
        const gridDash = options.gridDash;
        const plotBounds = options.plotBounds;
        const gridDomain = normalizeDomain(options.rightAxis?.domain) ?? normalizeDomain(options.leftAxis?.domain);
        const gridScale = options.rightAxis?.scale ?? options.leftAxis?.scale;
        // A1: each direction gets its own colour and its own visibility flag.
        // A missing key means "visible" (legacy behaviour) and a missing colour
        // means "use `gridColor`".
        const horizontalVisible = options.gridVisible?.horizontal !== false;
        const verticalVisible = options.gridVisible?.vertical !== false;
        const horizontalPalette = options.gridColorHorizontal
            ? { ...activePalette, grid: options.gridColorHorizontal }
            : activePalette;
        const verticalPalette = options.gridColorVertical
            ? { ...activePalette, grid: options.gridColorVertical }
            : activePalette;
        if (gridDomain && horizontalVisible) {
            renderHorizontalGridLines(ctx, plotBounds, gridDomain, horizontalPalette, gridDash, options.gridWidth, gridScale);
            // T2-01: minor horizontal grid (dashed, subtle) when height > 300 and subtle differs
            if (plotBounds.height > 300 && palette.gridSubtle && palette.gridSubtle !== palette.grid) {
                renderHorizontalGridLines(ctx, plotBounds, gridDomain, { ...horizontalPalette, grid: palette.gridSubtle }, "4 4", 0.5, gridScale);
            }
        }
        if (options.visibleBarRange && verticalVisible) {
            renderVerticalGridLines(ctx, plotBounds, options.visibleBarRange, verticalPalette, gridDash, options.gridWidth);
        }
        ctx.save();
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        ctx.strokeStyle = activePalette.axisBorder;
        if (options.leftAxis) {
            const x = options.leftAxis.bounds.left + options.leftAxis.bounds.width - 0.5;
            ctx.beginPath();
            ctx.moveTo(x, options.leftAxis.bounds.top);
            ctx.lineTo(x, options.leftAxis.bounds.top + options.leftAxis.bounds.height);
            ctx.stroke();
        }
        if (options.rightAxis) {
            const x = options.rightAxis.bounds.left + 0.5;
            ctx.beginPath();
            ctx.moveTo(x, options.rightAxis.bounds.top);
            ctx.lineTo(x, options.rightAxis.bounds.top + options.rightAxis.bounds.height);
            ctx.stroke();
        }
        ctx.restore();
        if (options.leftAxis) {
            renderAxisLabels(ctx, "left", options.leftAxis, activePalette, options.axisLabelFormatters?.left);
        }
        if (options.rightAxis) {
            renderAxisLabels(ctx, "right", options.rightAxis, activePalette, options.axisLabelFormatters?.right);
        }
    }
}
